import React from 'react';
import { View, Text, Dimensions } from 'react-native';
import Svg, {
  Line,
  Circle,
  Rect,
  Text as SvgText,
  Path,
  G,
} from 'react-native-svg';

const { width: screenWidth } = Dimensions.get('window');

interface ChartDataPoint {
  label: string;
  value: number;
}

interface LineChartProps {
  data: ChartDataPoint[];
  height?: number;
  width?: number;
  color?: string;
  showLabels?: boolean;
}

export const LineChart: React.FC<LineChartProps> = ({
  data,
  height = 200,
  width = screenWidth - 32,
  color = '#3B82F6',
  showLabels = true,
}) => {
  if (data.length === 0) {
    return (
      <View className='items-center justify-center' style={{ height }}>
        <Text className='text-gray-500'>No data available</Text>
      </View>
    );
  }

  const maxValue = Math.max(...data.map(d => d.value));
  const minValue = Math.min(...data.map(d => d.value));
  const valueRange = maxValue - minValue || 1;

  const chartWidth = width - 60; // Leave space for labels
  const chartHeight = height - 40; // Leave space for labels
  const stepX = chartWidth / (data.length - 1 || 1);

  const getY = (value: number) => {
    return chartHeight - ((value - minValue) / valueRange) * chartHeight + 20;
  };

  const getX = (index: number) => {
    return index * stepX + 30;
  };

  // Generate path for line
  const pathData = data
    .map((point, index) => {
      const x = getX(index);
      const y = getY(point.value);
      return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
    })
    .join(' ');

  return (
    <View>
      <Svg width={width} height={height}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => {
          const y = 20 + ratio * chartHeight;
          return (
            <Line
              key={index}
              x1={30}
              y1={y}
              x2={width - 30}
              y2={y}
              stroke='#E5E7EB'
              strokeWidth={1}
            />
          );
        })}

        {/* Line */}
        <Path d={pathData} stroke={color} strokeWidth={2} fill='none' />

        {/* Data points */}
        {data.map((point, index) => (
          <Circle
            key={index}
            cx={getX(index)}
            cy={getY(point.value)}
            r={4}
            fill={color}
          />
        ))}

        {/* Y-axis labels */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => {
          const value = minValue + ratio * valueRange;
          const y = 20 + ratio * chartHeight;
          return (
            <SvgText
              key={index}
              x={25}
              y={y + 4}
              fontSize={10}
              fill='#6B7280'
              textAnchor='end'
            >
              {value.toFixed(1)}
            </SvgText>
          );
        })}
      </Svg>

      {/* X-axis labels */}
      {showLabels && (
        <View className='flex-row justify-between px-8 mt-2'>
          {data.map((point, index) => (
            <Text
              key={index}
              className='text-xs text-gray-500 text-center'
              style={{ width: stepX }}
            >
              {point.label}
            </Text>
          ))}
        </View>
      )}
    </View>
  );
};

interface BarChartProps {
  data: ChartDataPoint[];
  height?: number;
  width?: number;
  color?: string;
  showLabels?: boolean;
}

export const BarChart: React.FC<BarChartProps> = ({
  data,
  height = 200,
  width = screenWidth - 32,
  color = '#3B82F6',
  showLabels = true,
}) => {
  if (data.length === 0) {
    return (
      <View className='items-center justify-center' style={{ height }}>
        <Text className='text-gray-500'>No data available</Text>
      </View>
    );
  }

  const maxValue = Math.max(...data.map(d => d.value));
  const chartWidth = width - 60;
  const chartHeight = height - 40;
  const barWidth = (chartWidth / data.length) * 0.8;
  const barSpacing = (chartWidth / data.length) * 0.2;

  return (
    <View>
      <Svg width={width} height={height}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => {
          const y = 20 + ratio * chartHeight;
          return (
            <Line
              key={index}
              x1={30}
              y1={y}
              x2={width - 30}
              y2={y}
              stroke='#E5E7EB'
              strokeWidth={1}
            />
          );
        })}

        {/* Bars */}
        {data.map((point, index) => {
          const barHeight = (point.value / maxValue) * chartHeight;
          const x = 30 + index * (barWidth + barSpacing) + barSpacing / 2;
          const y = 20 + chartHeight - barHeight;

          return (
            <Rect
              key={index}
              x={x}
              y={y}
              width={barWidth}
              height={barHeight}
              fill={color}
              rx={2}
            />
          );
        })}

        {/* Y-axis labels */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => {
          const value = ratio * maxValue;
          const y = 20 + ratio * chartHeight;
          return (
            <SvgText
              key={index}
              x={25}
              y={y + 4}
              fontSize={10}
              fill='#6B7280'
              textAnchor='end'
            >
              {value.toFixed(1)}
            </SvgText>
          );
        })}
      </Svg>

      {/* X-axis labels */}
      {showLabels && (
        <View className='flex-row justify-between px-8 mt-2'>
          {data.map((point, index) => (
            <Text
              key={index}
              className='text-xs text-gray-500 text-center'
              style={{ width: barWidth + barSpacing }}
            >
              {point.label}
            </Text>
          ))}
        </View>
      )}
    </View>
  );
};

interface RadarChartProps {
  data: Array<{
    label: string;
    value: number;
  }>;
  size?: number;
  color?: string;
}

export const RadarChart: React.FC<RadarChartProps> = ({
  data,
  size = 200,
  color = '#3B82F6',
}) => {
  if (data.length === 0) {
    return (
      <View
        className='items-center justify-center'
        style={{ width: size, height: size }}
      >
        <Text className='text-gray-500'>No data available</Text>
      </View>
    );
  }

  const center = size / 2;
  const radius = size / 2 - 40;
  const angleStep = (2 * Math.PI) / data.length;

  const getPoint = (value: number, index: number) => {
    const angle = index * angleStep - Math.PI / 2; // Start from top
    const r = (value / 1) * radius; // Assuming max value is 1
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle),
    };
  };

  const getLabelPoint = (index: number) => {
    const angle = index * angleStep - Math.PI / 2;
    const r = radius + 20;
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle),
    };
  };

  // Generate path for radar area
  const pathData =
    data
      .map((point, index) => {
        const { x, y } = getPoint(point.value, index);
        return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
      })
      .join(' ') + ' Z';

  return (
    <View className='items-center'>
      <Svg width={size} height={size}>
        {/* Grid circles */}
        {[0.2, 0.4, 0.6, 0.8, 1].map((ratio, index) => (
          <Circle
            key={index}
            cx={center}
            cy={center}
            r={radius * ratio}
            stroke='#E5E7EB'
            strokeWidth={1}
            fill='none'
          />
        ))}

        {/* Grid lines */}
        {data.map((_, index) => {
          const { x, y } = getPoint(1, index);
          return (
            <Line
              key={index}
              x1={center}
              y1={center}
              x2={x}
              y2={y}
              stroke='#E5E7EB'
              strokeWidth={1}
            />
          );
        })}

        {/* Data area */}
        <Path
          d={pathData}
          fill={color}
          fillOpacity={0.3}
          stroke={color}
          strokeWidth={2}
        />

        {/* Data points */}
        {data.map((point, index) => {
          const { x, y } = getPoint(point.value, index);
          return <Circle key={index} cx={x} cy={y} r={4} fill={color} />;
        })}

        {/* Labels */}
        {data.map((point, index) => {
          const { x, y } = getLabelPoint(index);
          return (
            <SvgText
              key={index}
              x={x}
              y={y}
              fontSize={10}
              fill='#6B7280'
              textAnchor='middle'
            >
              {point.label}
            </SvgText>
          );
        })}
      </Svg>
    </View>
  );
};

interface PieChartDataPoint {
  name: string;
  value: number;
  color: string;
}

interface PieChartProps {
  data: PieChartDataPoint[];
  size?: number;
  showLabels?: boolean;
}

export const PieChart: React.FC<PieChartProps> = ({
  data,
  size = 200,
  showLabels = true,
}) => {
  if (data.length === 0) {
    return (
      <View
        className='items-center justify-center'
        style={{ width: size, height: size }}
      >
        <Text className='text-gray-500'>No data available</Text>
      </View>
    );
  }

  const center = size / 2;
  const radius = size / 2 - 20;
  const total = data.reduce((sum, item) => sum + item.value, 0);

  let currentAngle = 0;

  const getPathData = (value: number, index: number) => {
    const percentage = value / total;
    const angle = percentage * 2 * Math.PI;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;

    const x1 = center + radius * Math.cos(startAngle);
    const y1 = center + radius * Math.sin(startAngle);
    const x2 = center + radius * Math.cos(endAngle);
    const y2 = center + radius * Math.sin(endAngle);

    const largeArcFlag = angle > Math.PI ? 1 : 0;

    const pathData = [
      `M ${center} ${center}`,
      `L ${x1} ${y1}`,
      `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
      'Z',
    ].join(' ');

    currentAngle += angle;
    return pathData;
  };

  return (
    <View className='items-center'>
      <Svg width={size} height={size}>
        {data.map((item, index) => {
          const pathData = getPathData(item.value, index);
          return (
            <Path
              key={index}
              d={pathData}
              fill={item.color}
              stroke='#fff'
              strokeWidth={2}
            />
          );
        })}
      </Svg>

      {showLabels && (
        <View className='mt-4'>
          {data.map((item, index) => (
            <View key={index} className='flex-row items-center mb-2'>
              <View
                className='w-3 h-3 rounded-full mr-2'
                style={{ backgroundColor: item.color }}
              />
              <Text className='text-sm text-gray-700'>
                {item.name}: {((item.value / total) * 100).toFixed(1)}%
              </Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
};
